/*
 * Maps the scanned-page `qrData` onto the single display model the event preview
 * renders from. Mirrors getLiveDisplay() in the qr-web-frontend reference.
 *
 * The backend now serves a *structured / nested* event model
 * (`event_information`, `event_date`, `location`, `contact_details`,
 * `facilities`, `colors`, `fonts`, ...). This adapter reads that shape and also
 * keeps a fallback for the older *flat* shape so both payloads render. All the
 * card components stay untouched — they only consume the returned display model.
 */

import linkFixer from "../../../../../utils/LinkFixer";
import capitalizeParagraph from "../../../../../utils/capitalizeParagraph";
import { convertWallClockToZone } from "./ics-builder";

export const isFilled = (value) =>
  typeof value === "string" && value.trim().length > 0;

/* Address/location parts (street number, postal code) can legitimately arrive
 * as numbers. isFilled stays string-only on purpose — the date/time/url/asset
 * helpers below rely on it meaning "non-empty string" and call .trim()/.replace()/
 * .includes() on the value. This variant additionally accepts a finite number
 * so numeric address parts aren't dropped from the address / map query. */
const isFilledPart = (value) =>
  isFilled(value) || (typeof value === "number" && Number.isFinite(value));

/* Pick the first "present" value across fallbacks. Unlike `||` it keeps a
 * numeric 0 (a valid street number / coordinate), and unlike `??` it still
 * skips an empty string. */
const coalescePart = (...values) => values.find(isFilledPart);

/* IANA timezone identifiers use underscores, never spaces ("America/New_York").
 * Some payloads arrive with a space; normalize once so the visible label and
 * the add-to-calendar payload never disagree. Returns null for empty input so
 * callers can use it directly as a display label. */
const normalizeTimeZone = (value) =>
  isFilled(value) ? value.replace(/ /g, "_") : null;

/*
 * The builder sends `gmt_time` as a display offset ("GMT+01:00") and the IANA
 * id in `time_zone`. add-to-calendar-button only accepts IANA names (or GMT/UTC
 * with no offset) — a GMT±HH:MM label throws "invalid time zone given" and the
 * Add to Calendar popup never opens.
 */
const GMT_OFFSET_LABEL_PATTERN = /^(GMT|UTC)[+-]/i;

const isIanaTimeZone = (value) => {
  if (!isFilled(value) || GMT_OFFSET_LABEL_PATTERN.test(value)) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: value });
    return true;
  } catch {
    return false;
  }
};

/*
 * First filled candidate is the on-screen label (offset or IANA). Calendar
 * config must skip offset labels and use the first real IANA id instead.
 */
const pickTimeZoneFields = (...candidates) => {
  const normalized = candidates.map(normalizeTimeZone).filter(Boolean);

  return {
    displayTimeZone: normalized[0] ?? null,
    calendarTimeZone: normalized.find(isIanaTimeZone) ?? null,
  };
};

const resolveCreatorTimeZone = (value) => {
  const normalized = normalizeTimeZone(value);
  return isIanaTimeZone(normalized) ? normalized : null;
};

const resolveViewerTimeZone = (viewerTimeZone) => {
  const requested = normalizeTimeZone(viewerTimeZone);
  if (isIanaTimeZone(requested)) return requested;

  try {
    const runtime = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isIanaTimeZone(runtime) ? runtime : null;
  } catch {
    return null;
  }
};

/*
 * The selector-off clock is shown in the viewer's zone, with no zone label.
 * Each side converts on its own so a midnight crossing can move only that
 * date. Add to Calendar keeps the original entries.
 */
const shiftEntriesToViewerZone = (entries, sourceTimeZone, viewerTimeZone) => {
  if (!sourceTimeZone || !viewerTimeZone || sourceTimeZone === viewerTimeZone) {
    return entries;
  }

  return (entries || []).map((entry) => {
    const start = convertWallClockToZone(
      entry?.startDate,
      entry?.startTime,
      sourceTimeZone,
      viewerTimeZone,
    );
    const end = convertWallClockToZone(
      entry?.endDate,
      entry?.endTime,
      sourceTimeZone,
      viewerTimeZone,
    );

    return {
      ...entry,
      ...(start ? { startDate: start.date, startTime: start.time } : {}),
      ...(end ? { endDate: end.date, endTime: end.time } : {}),
    };
  });
};

/*
 * The time card shows an offset such as "GMT+03:00", matching the creation
 * preview. The published event only stores an IANA id ("Europe/Istanbul"),
 * which Add to Calendar still needs, so this builds the offset label from
 * that id. The event's start date is used because daylight saving changes
 * the offset through the year: the same zone can be GMT+02:00 in summer and
 * GMT+01:00 in winter. A label that is already an offset is left as-is.
 */
const formatGmtOffsetLabel = (timeZone, startDate) => {
  if (!isIanaTimeZone(timeZone)) return timeZone;

  const trimmedStartDate = isFilled(startDate) ? startDate.trim() : "";
  const referenceDate = /^\d{4}-\d{2}-\d{2}$/.test(trimmedStartDate)
    ? new Date(`${trimmedStartDate}T12:00:00Z`)
    : new Date();

  if (Number.isNaN(referenceDate.getTime())) return timeZone;

  try {
    const offset = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "longOffset",
    })
      .formatToParts(referenceDate)
      .find((part) => part.type === "timeZoneName")?.value;

    return offset && offset !== timeZone ? offset : timeZone;
  } catch {
    return timeZone;
  }
};

const resolveImplicitCreatorZone = (explicitFields, creatorTimeZone) => {
  const creatorZone = resolveCreatorTimeZone(creatorTimeZone);
  const shiftDisplayToViewer =
    !explicitFields.displayTimeZone &&
    !explicitFields.calendarTimeZone &&
    Boolean(creatorZone);

  return {
    displayTimeZone: explicitFields.displayTimeZone,
    calendarTimeZone:
      explicitFields.calendarTimeZone ||
      (shiftDisplayToViewer ? creatorZone : null),
    shiftDisplayToViewer,
    creatorZone,
  };
};

const buildContactDisplayItem = ({ label, value, href }) => {
  const displayLabel = isFilled(label) ? label : undefined;
  const displayValue = isFilled(value) ? value : "";

  if (!isFilled(displayLabel) && !isFilled(displayValue)) return null;

  return {
    ...(displayLabel ? { label: displayLabel } : {}),
    value: displayValue,
    ...(isFilled(displayValue) && href ? { href } : {}),
  };
};

/*
 * The event summary is free text: cap the *rendered* length so an overlong
 * paragraph can't push the rest of the page off-screen. Only the display value
 * is trimmed — the calendar payload keeps the full description.
 *
 * We count by code points (so surrogate pairs / emoji are never split
 * mid-character) and reserve one slot for the trailing ellipsis, so the
 * rendered summary never exceeds `max` characters (keep max-1 code points +
 * "…" = max). The scan stops as soon as we pass `max`, so a very long
 * description is never fully walked or copied into an intermediate array.
 */
const SUMMARY_MAX_LENGTH = 500;

const truncate = (value, max) => {
  if (typeof value !== "string") return value;
  /* UTF-16 length is an upper bound on the code-point count, so a string no
   * longer than `max` code units can't exceed `max` code points — skip the
   * scan entirely for the common (short) case. */
  if (value.length <= max) return value;

  let count = 0;
  let cutIndex = value.length; // UTF-16 index just after the (max-1)-th code point
  for (let i = 0; i < value.length;) {
    if (count === max - 1) cutIndex = i;
    count += 1;
    if (count > max) break; // more than `max` code points -> must truncate
    i += value.codePointAt(i) > 0xffff ? 2 : 1;
  }
  if (count <= max) return value;
  return `${value.slice(0, cutIndex).trimEnd()}\u2026`;
};

/*
 * Builder / API payloads send either the CSS family label ("Open Sans") or the
 * option id ("open-sans"). The id is not a loaded font-family, so the browser
 * falls back to Times. Map ids onto the families registered in Preview/index.css
 * and App.js WebFont.load, then quote so multi-word names stay valid CSS.
 */
const FONT_FAMILY_BY_VALUE = {
  "gt-walsheim-pro": "GT Walsheim Pro",
  lato: "Lato",
  "open-sans": "Open Sans",
  roboto: "Roboto",
  oswald: "Oswald",
  montserrat: "Montserrat",
  "source-sans-3": "Source Sans 3",
  "slabo-27px": "Slabo 27px",
  raleway: "Raleway",
  merriweather: "Merriweather",
  "noto-sans": "Noto Sans",
};

const quoteFont = (value) => {
  if (!isFilled(value)) return null;
  const trimmed = value.trim();
  const family = FONT_FAMILY_BY_VALUE[trimmed.toLowerCase()] || trimmed;
  return `'${family}'`;
};

/*
 * Resolve a stored asset reference to a loadable URL. The nested/event API serves
 * bare relative paths (e.g. `uploads/screen/abc.png`). Left as-is, the browser
 * resolves them against the current `/p/<uid>` route, hitting the SPA fallback
 * instead of the file.
 *
 * Uploads live on the filesystem CDN (Minio locally, S3 in deployed envs) —
 * the same host the builder uses via `withFilesystemCdn`. Prefixing them with
 * the QR website origin (`REACT_APP_QR_WEBSITE_URL`) 404s: the PHP site does
 * not serve those files. Fall back to the website origin only when the CDN
 * env is unset. Other root-relative paths (`/images/...`) stay on this
 * origin. When every env base is missing, force a leading `/` so the request
 * never resolves as a sibling of `/p/<uid>`.
 */
const trimOrigin = (value) => (value || "").replace(/\/+$/, "");

const uploadAssetBase = () =>
  trimOrigin(process.env.REACT_APP_FILESYSTEM_CDN_URL) ||
  trimOrigin(process.env.REACT_APP_QR_WEBSITE_URL);

const toAssetUrl = (path) => {
  if (!isFilled(path)) return null;
  const trimmed = path.trim();
  if (/^(https?:|data:|blob:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;

  const relative = trimmed.replace(/^\/+/, "");
  const isUpload =
    relative.startsWith("uploads/") || relative.includes("/uploads/");
  if (trimmed.startsWith("/") && !isUpload) return trimmed;

  const base = uploadAssetBase();
  return base ? `${base}/${relative}` : `/${relative}`;
};

/* Same grid order as Business / the frontend FacilityIcons list. */
const CANONICAL_FACILITY_ORDER = [
  "wifi",
  "seating",
  "accessible",
  "restroom",
  "child-friendly",
  "pet-friendly",
  "parking",
  "public-transport",
  "taxi",
  "lodging",
  "coffee",
  "bar",
  "restaurant",
  "gym",
  "outdoor-terrace",
];

const sortFacilitiesByCanonicalOrder = (facilities) => {
  const present = new Set((facilities || []).filter(isFilled));
  return CANONICAL_FACILITY_ORDER.filter((id) => present.has(id));
};

/* Reverse of FACILITY_ICON_BY_ID in qr-web-frontend's form-to-scan-page mapper.
 * Only used for the legacy flat payload, where facilities arrive as `ficon*`. */
const FACILITY_KEY_BY_FICON = {
  ficon: "wifi",
  ficon1: "seating",
  ficon2: "accessible",
  ficon3: "restroom",
  ficon4: "child-friendly",
  ficon5: "pet-friendly",
  ficon6: "parking",
  ficon7: "public-transport",
  ficon8: "taxi",
  ficon9: "lodging",
  ficon10: "coffee",
  ficon11: "bar",
  ficon12: "restaurant",
  ficon13: "gym",
  ficon14: "outdoor-terrace",
};

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/*
 * `new Date("YYYY-MM-DD")` is parsed as UTC midnight per the spec, so formatting
 * it in a timezone behind UTC shifts the displayed day back by one. Parse
 * date-only strings as a *local* date instead; other inputs fall back to the
 * native parser.
 */
const parseLocalDate = (value) => {
  if (DATE_ONLY_RE.test(value.trim())) {
    const [year, month, day] = value.trim().split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(value);
};

const formatEventDate = (value, locale) => {
  if (!isFilled(value)) return null;
  const date = parseLocalDate(value);
  if (!(date instanceof Date) || isNaN(date)) return value;
  try {
    /* Let toLocaleDateString place the year per locale instead of appending a
     * hard-coded ", YYYY" suffix, which isn't correct for every language. */
    return date.toLocaleDateString(locale || undefined, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return value;
  }
};

/* yyyy-mm-dd for the add-to-calendar library. */
const formatCalendarDate = (value) => {
  if (!isFilled(value)) return " ";
  /* Already in the expected format — return verbatim so timezone parsing can
   * never shift the day. */
  if (DATE_ONLY_RE.test(value.trim())) return value.trim();
  const date = new Date(value);
  /* Unparseable values must not reach the calendar payload: fall back to the
   * " " placeholder instead of a value the add-to-calendar library can't use. */
  if (!(date instanceof Date) || isNaN(date)) return " ";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const CALENDAR_PLACEHOLDER = " ";

const isPresentCalendarDate = (value) =>
  isFilled(value) && value !== CALENDAR_PLACEHOLDER;

/* The "Add to Calendar" button is shown whenever at least one date record has
 * both a start and an end. formatCalendarDate returns the placeholder for
 * missing/invalid dates. Multi-record payloads use `dates` instead of the
 * top-level start/end pair. */
const hasCalendarDates = (calendarConfig) => {
  if (Array.isArray(calendarConfig.dates) && calendarConfig.dates.length > 0) {
    return calendarConfig.dates.some(
      (entry) =>
        isPresentCalendarDate(entry.startDate) &&
        isPresentCalendarDate(entry.endDate),
    );
  }

  return (
    isPresentCalendarDate(calendarConfig.startDate) &&
    isPresentCalendarDate(calendarConfig.endDate)
  );
};

const formatTimeTo12Hour = (time) => {
  if (!isFilled(time)) return null;
  /* Anything beyond digits, ":" and whitespace is an already-formatted, often
   * localized display string (English "3:00 pm", French "09 h 00", Arabic
   * "09:00 صباحا", Japanese "午前9:00時", German "9:00 morgens", ...). Return it
   * verbatim so we never re-parse it into an English "AM/PM" value, which would
   * drop the locale suffix or even flip PM->AM. */
  if (/[^\d\s:]/.test(time)) return time;

  let normalized = time;
  if (!normalized.includes(":")) normalized = `${normalized}:00`;

  const [hoursStr, minutesStr] = normalized.split(":");
  let hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);
  if (isNaN(hours) || isNaN(minutes)) return null;

  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${ampm}`;
};

/*
 * The add-to-calendar widget expects machine-friendly 24h "HH:MM" times, but
 * the incoming value (template i18n strings or backend payloads) can be a 12h
 * "hh:mm AM/PM" string. Normalize to 24h here so the calendar payload is valid
 * regardless of how the time was provided; the display layer formats times
 * separately via formatTimeTo12Hour.
 */
const to24Hour = (value) => {
  /* Mirror formatCalendarDate: return the " " placeholder (not null) for empty
   * values so the add-to-calendar payload stays consistent. */
  if (!isFilled(value)) return " ";
  /* Normalize localized "h" separators ("09 h 00" / "17h00" -> "09:00"). */
  const normalized = value.trim().replace(/(\d)\s*[hH]\s*(\d)/, "$1:$2");
  /* Grab the leading HH:MM even when a (localized) suffix trails it. */
  const match = normalized.match(/(\d{1,2}):(\d{2})/);
  /* Unparseable strings must not reach the calendar payload: fall back to the
   * library placeholder rather than emitting an invalid time. */
  if (!match) return " ";
  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (hours > 23 || minutes > 59) return " ";
  /* Convert to 24h when an AM/PM marker is present, covering English plus the
   * localized markers used by our templates (Arabic, Japanese, German, French).
   * With no marker the value is assumed to already be 24h. */
  const hasPM =
    /p\.?\s*m\.?|مساء|午後|nachmittags?|abends?|apr[eè]s-?midi|soir/i.test(
      value,
    );
  const hasAM = /a\.?\s*m\.?|صباح|午前|morgens?|vormittags?|matin/i.test(value);
  if (hasPM && hours < 12) hours += 12;
  if (hasAM && hours === 12) hours = 0;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const isTruthyFlag = (value) =>
  value === true || value === "true" || value === 1 || value === "1";

const buildDateEntry = (dateValue, timeValue, locale, { hideTime } = {}) => {
  const date = formatEventDate(dateValue, locale);
  const time = hideTime ? null : formatTimeTo12Hour(timeValue);
  if (!date && !time) return null;
  return { date, time };
};

/*
 * An event can carry several date entries. Each raw entry is normalized to
 * { startDate, startTime, endDate, endTime } before reaching here; this builds
 * the ordered list of { start, end } groups the date card renders, dropping any
 * group with neither a start nor an end so an empty row never shows.
 */
const buildDateGroups = (entries, locale, { hideTime } = {}) =>
  (entries || [])
    .map((entry) => {
      const start = buildDateEntry(
        entry?.startDate,
        entry?.startTime,
        locale,
        { hideTime },
      );
      const end = buildDateEntry(entry?.endDate, entry?.endTime, locale, {
        hideTime,
      });
      if (!start && !end) return null;
      return { start, end };
    })
    .filter(Boolean);

/*
 * add-to-calendar-button treats a config with dates but no times as an all-day
 * event. Sending the " " placeholder (used for missing dates) as startTime/
 * endTime is invalid and the widget silently no-ops — which is why All Day
 * events showed the button but did nothing (OQG3-2567).
 *
 * Omit times when the payload flags all-day, or when both times are empty
 * (implicit all-day). Non-empty but unparseable times still go through
 * to24Hour so existing placeholder fallbacks keep working.
 */
const buildCalendarTimeFields = (startTime, endTime, isAllDay) => {
  if (isTruthyFlag(isAllDay) || (!isFilled(startTime) && !isFilled(endTime))) {
    return {};
  }
  return {
    startTime: to24Hour(startTime),
    endTime: to24Hour(endTime),
  };
};

const toCalendarRecord = (entry, isAllDay) => ({
  startDate: formatCalendarDate(entry?.startDate),
  endDate: formatCalendarDate(entry?.endDate),
  ...buildCalendarTimeFields(entry?.startTime, entry?.endTime, isAllDay),
});

const isCompleteCalendarRecord = (record) =>
  isPresentCalendarDate(record.startDate) &&
  isPresentCalendarDate(record.endDate);

/*
 * Business Requirement:
 * OQG3-2559: Add to Calendar must cover every configured date record, not
 * only the first. Each form row stays its own calendar event so a gap
 * between records is not filled in. A single row still uses the top-level
 * start/end fields; two or more complete rows go in `dates` for the
 * add-to-calendar-button multi-event payload.
 */
const buildCalendarDateFields = (entries, isAllDay) => {
  const filled = (entries || []).filter(
    (entry) => isFilled(entry?.startDate) || isFilled(entry?.endDate),
  );

  if (filled.length === 0) {
    return {
      startDate: CALENDAR_PLACEHOLDER,
      endDate: CALENDAR_PLACEHOLDER,
    };
  }

  const completeRecords = filled
    .map((entry) => toCalendarRecord(entry, isAllDay))
    .filter(isCompleteCalendarRecord);

  if (completeRecords.length > 1) {
    return { dates: completeRecords };
  }

  if (completeRecords.length === 1) {
    return completeRecords[0];
  }

  return toCalendarRecord(filled[0], isAllDay);
};

/*
 * Technical Detail:
 * add-to-calendar-button 3.x copies the top-level `name` into a date record
 * only when there is exactly one record; with several it reads each record's
 * own `name` and crashes on undefined while writing the .ics SUMMARY. Give
 * every record the event title so the multi-date file generates.
 */
const withRecordNames = (dateFields, name) => {
  if (!Array.isArray(dateFields.dates)) return dateFields;
  return {
    dates: dateFields.dates.map((record) => ({ ...record, name })),
  };
};

/*
 * Business Requirement:
 * OQG3-2559 / OQG3-2560: when an event has no usable zone at all, the clock
 * times mean "on the visitor's clock". Without a zone the library would
 * default to GMT and shift the time for every visitor.
 *
 * Technical Detail:
 * "currentBrowser" makes add-to-calendar-button resolve the visitor's
 * timezone at click time and use it as the event zone without altering the
 * clock values. A selected zone, or the creator's zone when the selector is
 * left off, always wins over this fallback.
 */
const BROWSER_TIME_ZONE = "currentBrowser";

const buildCalendarTimeZoneField = (timeZone) => ({
  timeZone: timeZone || BROWSER_TIME_ZONE,
});

/*
 * Business Requirement:
 * OQG3-2575: calendar apps treat a space as a real location. When the event
 * has no address, leave location off the payload. Trim a real address so
 * leading or trailing whitespace is not saved as the place.
 *
 * Technical Detail:
 * Dates still use the " " placeholder because add-to-calendar-button rejects
 * an empty date. Location is optional, so the field is omitted instead.
 */
const buildCalendarLocationField = (address) => {
  if (!isFilled(address)) return {};
  return { location: address.trim() };
};

const normalizeWebsiteHref = (url) => {
  if (!isFilled(url)) return undefined;
  const trimmed = url.trim();
  /* Compare against the placeholder *after* trimming so "  #  " is also treated
   * as no link instead of becoming "https://#". */
  if (trimmed === "#") return undefined;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  /* Protocol-relative input (e.g. "//example.com", produced by linkFixer
   * elsewhere) only needs a scheme, not another "//" — otherwise it becomes
   * the invalid "https:////example.com". */
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `https://${trimmed}`;
};

/*
 * Link buttons render the URL straight into an <a href>, so scheme-less values
 * (e.g. "www.example.com") would otherwise resolve as broken in-app relative
 * paths. Normalize with linkFixer like the other link views, while preserving
 * the "#" placeholder (EventHeroCard treats it as a non-navigating button) and
 * collapsing blank/whitespace-only values to undefined so the UI renders the
 * non-navigating button path instead of an <a href="   ">.
 */
const normalizeLinkUrl = (url) => {
  if (!isFilled(url)) return undefined;
  const trimmed = url.trim();
  /* "#" is the non-navigating placeholder; any leading-"/" URL — protocol-
   * relative ("//host") or root-relative ("/path") — is returned as-is because
   * linkFixer would prepend "//" and produce "////host" / "///path". */
  if (trimmed === "#" || trimmed.startsWith("/")) return trimmed;
  return linkFixer(trimmed);
};

/* Prefer the current field, but fall back to a legacy array so contact rows
 * keep rendering for older flat payloads that predate the newer field name. */
const firstNonEmptyList = (primary, fallback) =>
  Array.isArray(primary) && primary.length > 0 ? primary : fallback || [];

const trimmedParts = (parts) =>
  parts
    .filter(isFilledPart)
    .map((part) => String(part).trim())
    .filter((part) => part.length > 0);

const joinAddress = (parts) => {
  const line = trimmedParts(parts).join(", ");
  return line.length > 0 ? line : null;
};

/*
 * Maps the scanned-page `socialLinks` shape ({ name: url, icon: platform,
 * url: iconImage, text: subline }) onto the social blocks the preview renders.
 * Mirrors the reference EventSocialNetworks default blocks (icon + platform
 * name + "Social Account" subline). `twitter` renders as "X" to match the rest
 * of the scanned page.
 */
/*
 * Authoritative platform → icon-id / title maps, mirrored 1:1 from the
 * qr-web-frontend social source of truth (utils/constants/social-images.ts).
 * The published event API returns social as `social_links: [{type,text,url}]`
 * with no icon URL, so the scanned page resolves the icon (and href) itself.
 */
const SOCIAL_ICON_IDS_BY_TYPE = {
  "apple-music": "icon31",
  doordash: "icon33",
  dribbble: "icon1",
  facebook: "icon2",
  "facebook-messenger": "icon24",
  flickr: "icon3",
  github: "icon4",
  gitlab: "icon37",
  google: "icon5",
  instagram: "icon20",
  line: "icon6",
  linkedin: "icon7",
  onlyfans: "icon32",
  "open-table": "icon28",
  pinterest: "icon8",
  postmates: "icon27",
  reddit: "icon9",
  signal: "icon35",
  snapchat: "icon11",
  soundcloud: "icon30",
  spotify: "icon29",
  telegram: "icon23",
  tiktok: "icon21",
  tripadvisor: "icon12",
  trustpilot: "icon34",
  tumblr: "icon13",
  "uber-eats": "icon26",
  vimeo: "icon15",
  vkontakte: "icon16",
  web: "icon17",
  wechat: "icon36",
  whatsapp: "icon22",
  x: "icon14",
  xing: "icon18",
  yelp: "icon25",
  youtube: "icon19",
};

const SOCIAL_TITLE_BY_TYPE = {
  web: "Web",
  dribbble: "Dribbble",
  facebook: "Facebook",
  flickr: "Flickr",
  github: "GitHub",
  gitlab: "GitLab",
  google: "Google Review",
  line: "Line",
  linkedin: "LinkedIn",
  pinterest: "Pinterest",
  reddit: "Reddit",
  snapchat: "Snapchat",
  tripadvisor: "TripAdvisor",
  tumblr: "Tumblr",
  x: "X",
  vimeo: "Vimeo",
  vkontakte: "Vkontakte",
  xing: "Xing",
  youtube: "YouTube",
  instagram: "Instagram",
  tiktok: "TikTok",
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  "facebook-messenger": "Facebook Messenger",
  yelp: "Yelp",
  "uber-eats": "Uber Eats",
  postmates: "Postmates",
  "open-table": "OpenTable",
  spotify: "Spotify",
  soundcloud: "SoundCloud",
  "apple-music": "Apple Music",
  onlyfans: "OnlyFans",
  doordash: "DoorDash",
  trustpilot: "Trustpilot",
  signal: "Signal",
  wechat: "WeChat",
};

/* Username-style inputs are expanded to full profile URLs (mirror buildSocialUrl). */
const SOCIAL_URL_PREFIXES = {
  whatsapp: "https://wa.me/",
  dribbble: "https://dribbble.com/",
  line: "https://line.me/R/ti/p/",
  snapchat: "https://www.snapchat.com/add/",
  instagram: "https://www.instagram.com/",
  x: "https://x.com/",
};

const QR_SITE_BASE_URL = (process.env.REACT_APP_QR_WEBSITE_URL || "").replace(
  /\/+$/,
  "",
);

const socialIconUrl = (type) => {
  const iconId = SOCIAL_ICON_IDS_BY_TYPE[type];
  return iconId
    ? `${QR_SITE_BASE_URL}/themes/altum/assets/images/socialicon/${iconId}.svg`
    : null;
};

const buildSocialHref = (type, url) => {
  const raw = (url || "").trim();
  /* No URL means the row stays non-clickable: return undefined (consistent with
   * the legacy branch) so callers only ever test one falsy value, instead of
   * "" — and never link to a bare platform prefix. */
  if (!raw) return undefined;
  if (!type) return raw;
  if (/^https?:\/\//i.test(raw)) return raw;
  /* Already protocol-relative ("//host/..."): return unchanged so linkFixer
   * doesn't turn it into an invalid "////host/..." href. */
  if (raw.startsWith("//")) return raw;
  const prefix = SOCIAL_URL_PREFIXES[type];
  /* Platforms without a username prefix (facebook, linkedin, ...) get a raw
   * value that may lack a scheme (e.g. "www.facebook.com/x"). Normalize it the
   * same way the rest of the app does so it opens externally instead of being
   * treated as an in-app relative link. */
  if (!prefix) return linkFixer(raw);
  const bareDomain = prefix.replace(/^https?:\/\//i, "").replace(/^www\./i, "");
  if (raw.replace(/^www\./i, "").startsWith(bareDomain))
    return `https://${raw}`;
  return `${prefix}${raw}`;
};

const buildSocialBlocks = (socialLinks) =>
  (socialLinks || [])
    .map((item) => {
      if (!item) return null;

      /* Nested/published event API shape: { type, text, url }. */
      if (isFilled(item.type)) {
        const type = item.type.toLowerCase();
        const icon = socialIconUrl(type);
        const title = SOCIAL_TITLE_BY_TYPE[type] || capitalizeParagraph(type);
        /* No resolvable icon means an unknown/unsupported platform. Drop it so
         * EventSocialNetworks never renders an <img> with an empty src. */
        if (!icon) return null;
        return {
          icon,
          title,
          description: isFilled(item.text) ? item.text : null,
          href: buildSocialHref(type, item.url),
        };
      }

      /* Legacy flat shape: { icon: platform, url: iconImage, name: href }. */
      const iconImage = isFilled(item.url) ? item.url : null;
      const platform = isFilled(item.icon) ? item.icon : null;
      /* Require both an icon image and a platform name: without the image
       * EventSocialNetworks would draw a broken <img src>, and without the
       * platform the row has no title/alt text and is unusable. */
      if (!iconImage || !platform) return null;
      const title = capitalizeParagraph(
        platform === "twitter" ? "X" : platform,
      );
      return {
        icon: iconImage,
        title,
        description: isFilled(item.text) ? item.text : null,
        /* Guard against `linkFixer(undefined)` producing a "//undefined" href
         * (linkFixer stringifies its input): only a real name becomes a link,
         * otherwise the row stays non-clickable. */
        href: isFilled(item.name) ? linkFixer(item.name) : undefined,
      };
    })
    .filter(Boolean);

const openExternal = (url) => {
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (opened) opened.opener = null;
};

/*
 * iOS opens map links in Apple Maps by default, so target maps.apple.com there
 * (iPadOS 13+ reports as "MacIntel", disambiguated by the touch-point count).
 * Everything else goes through Google Maps, which Android hands off to the app
 * and desktop opens on the web.
 */
const isIOS = () =>
  typeof navigator !== "undefined" &&
  (/iP(hone|od|ad)/.test(navigator.userAgent || "") ||
    (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1));

/*
 * Coordinates can arrive as strings (flat payload) or numbers (nested model).
 * Validate that each part is a finite number so non-numeric junk never lands
 * (unencoded) in the map URL, then normalize to a canonical string.
 */
const coordPart = (value) => {
  const raw =
    typeof value === "number" ? value : isFilled(value) ? value.trim() : null;
  if (raw === null || raw === "") return null;
  const num = Number(raw);
  return Number.isFinite(num) ? String(num) : null;
};

const hasCoords = (latitude, longitude) =>
  coordPart(latitude) !== null && coordPart(longitude) !== null;

const buildMapUrl = ({ query, latitude, longitude }) => {
  const lat = coordPart(latitude);
  const lng = coordPart(longitude);
  const coords = lat !== null && lng !== null ? `${lat},${lng}` : null;
  const ios = isIOS();
  const base = ios ? "https://maps.apple.com/" : "https://maps.google.com/";
  if (coords) {
    /* Prefer exact coordinates when the payload carries them; keep the address
     * as a human-readable label on Apple Maps. */
    return ios
      ? `${base}?ll=${coords}${isFilled(query) ? `&q=${encodeURIComponent(query)}` : ""}`
      : `${base}?q=${coords}`;
  }
  return `${base}?q=${encodeURIComponent(query)}`;
};

const buildShowMapHandler =
  ({ query, latitude, longitude, fallbackUrl } = {}) =>
  () => {
    if (hasCoords(latitude, longitude) || isFilled(query)) {
      openExternal(buildMapUrl({ query, latitude, longitude }));
    } else if (
      isFilled(fallbackUrl) &&
      fallbackUrl !== "#" &&
      /^(https?:)?\/\//i.test(fallbackUrl)
    ) {
      /* Only allow http(s)/protocol-relative targets so a user-controlled
       * fallback URL can't smuggle a javascript: (or other) scheme. */
      openExternal(fallbackUrl);
    }
  };

const CALENDAR_OPTIONS = [
  "Google",
  "Apple",
  "Yahoo",
  "Outlook.com",
  "Microsoft365",
];

/*
 * Technical Detail:
 * add-to-calendar-button injects styleLight into `:host{…}`. Closing that
 * wrapper first so these selectors apply in the shadow tree. 3.1.0 turns the
 * options list into a full-viewport sheet below 575px, which fills the ~312px
 * phone mockup. Keep a compact card, and force wrapping so sub-event rows
 * (fit-content + 13.5em min-width) do not overflow.
 */
const CALENDAR_WIDGET_STYLE_LIGHT =
  "} .atcb-modal{max-width:260px;min-height:0;box-sizing:border-box;margin-inline:auto}" +
  " .atcb-list.atcb-modal{width:100%;max-width:260px;min-width:0;min-height:0;margin:auto;border-radius:12px;font-size:14px}" +
  " .atcb-list.atcb-modal .atcb-list-item{gap:.45em;padding:.35em .6em;font-size:14px;line-height:1.35em}" +
  " .atcb-modal-box{width:100%;max-width:260px;min-height:0;box-sizing:border-box;font-size:14px}" +
  " .atcb-modal-content{padding-inline:12px}" +
  " .atcb-modal-content-subevents{width:100%;max-width:100%}" +
  " .atcb-subevent-btn{max-width:100%;box-sizing:border-box}" +
  " .atcb-date-btn-right{min-width:0}" +
  " .atcb-date-btn-headline{white-space:normal;overflow-wrap:anywhere}" +
  " .atcb-date-btn-content-text span:not(.atcb-icon-ical){display:inline;white-space:normal}" +
  " .atcb-list,.atcb-list-wrapper{max-width:100%;box-sizing:border-box}" +
  " .atcb-modal-host-initialized #atcb-bgoverlay{max-width:100%;overflow-x:hidden;padding:20px 12px;align-items:center;background-color:var(--overlay-background)}" +
  " :host{";

/* A single option makes atcb_action skip the platform list and fire that
 * option directly; "iCal" with a `dates` array writes one .ics that holds a
 * VEVENT per record and triggers the download. */
const MULTI_DATE_CALENDAR_OPTIONS = ["iCal"];

const ICS_FILE_NAME_FALLBACK = "event";
const ICS_FILE_NAME_MAX_LENGTH = 80;

/* Slug for the downloaded .ics (the library appends the extension). Strips
 * diacritics, collapses everything else to "-" and falls back when the title
 * is empty or non-Latin so the browser never receives a blank filename. */
const toIcsFileName = (title) => {
  if (!isFilled(title)) return ICS_FILE_NAME_FALLBACK;
  const slug = title
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, ICS_FILE_NAME_MAX_LENGTH)
    .replace(/-+$/g, "");
  return slug || ICS_FILE_NAME_FALLBACK;
};

/*
 * Business Requirement:
 * OQG3-2559: a single date keeps the calendar-platform modal so the event
 * opens pre-filled in Google/Apple/Outlook/Yahoo. Two or more separate date
 * records cannot be pre-filled through one provider URL, so the button
 * downloads a single .ics containing every record instead. The title slug
 * names every .ics the page hands out, whether the library or the iOS
 * fallback (ios-calendar-fallback.js) writes it.
 */
export const isMultiDateCalendarConfig = (calendarFields) =>
  Array.isArray(calendarFields?.dates) && calendarFields.dates.length > 1;

const buildCalendarWidgetChrome = (dateFields, title) => {
  const isMultiDate = isMultiDateCalendarConfig(dateFields);
  return {
    options: isMultiDate ? MULTI_DATE_CALENDAR_OPTIONS : CALENDAR_OPTIONS,
    iCalFileName: toIcsFileName(title),
    /* add-to-calendar-button omits the #atcb-reference Pro footer when set. */
    hideBranding: true,
    styleLight: CALENDAR_WIDGET_STYLE_LIGHT,
  };
};

/*
 * Detect the nested/live model by keys unique to it. We deliberately do NOT key
 * off `location` alone: the legacy/flat payloads also carry a `location` object,
 * so using it here would misroute them into the nested adapter and render empty.
 */
const isNestedModel = (data) =>
  Boolean(
    data &&
    (data.event_information ||
      data.event_date ||
      data.event_details ||
      data.contact_details ||
      data.colors ||
      data.fonts ||
      Array.isArray(data.facilities)),
  );

/* ---------------------------------------------------------------- nested ---- */

const getNestedDisplay = (data, { locale, viewMoreLabel, viewerTimeZone } = {}) => {
  const info = data.event_information || {};
  const eventDate = data.event_date || {};
  const details = data.event_details || {};
  const loc = data.location || {};
  const contact = data.contact_details || {};
  const colors = data.colors || {};
  const fonts = data.fonts || {};

  const links = (info.links || [])
    .map((item) => {
      /* Match the flat model: a missing label falls back to the localized
       * "View More" copy (then the raw URL) rather than showing the URL. */
      const label = isFilled(item?.label)
        ? item.label
        : isFilled(item?.url)
          ? viewMoreLabel || item.url
          : null;
      if (!isFilled(label) && !isFilled(item?.url)) return null;
      return { label, url: normalizeLinkUrl(item?.url) };
    })
    .filter(Boolean);

  const websites = (contact.websites || [])
    .map((item) => {
      return buildContactDisplayItem({
        label: item?.label,
        value: item?.url,
        href: normalizeWebsiteHref(item?.url),
      });
    })
    .filter(Boolean);

  const phones = (contact.phones || [])
    .map((item) => {
      return buildContactDisplayItem({
        label: item?.label,
        value: item?.number,
        href: `tel:${item?.number}`,
      });
    })
    .filter(Boolean);

  const emails = (contact.emails || [])
    .map((item) => {
      return buildContactDisplayItem({
        label: item?.label,
        value: item?.email,
        href: `mailto:${item?.email}`,
      });
    })
    .filter(Boolean);

  const streetParts = loc.is_street_number_first
    ? [loc.number, loc.street]
    : [loc.street, loc.number];
  const address = joinAddress([
    trimmedParts(streetParts).join(" "),
    loc.city,
    loc.postal_code,
    loc.state_or_province,
    loc.country,
  ]);
  const mapQuery = [
    loc.street,
    loc.number,
    loc.postal_code,
    loc.city,
    loc.state_or_province,
    loc.country,
  ]
    .filter(isFilledPart)
    .join(" ");

  const primaryColor = isFilled(colors.primary) ? colors.primary : null;
  const secondaryColor = isFilled(colors.secondary) ? colors.secondary : null;
  const titleFont = quoteFont(fonts.title);
  const textFont = quoteFont(fonts.texts || fonts.text);

  /* A selected zone is the label and the calendar zone. When the selector is
   * left off, hide the label and shift the on-screen clock into the viewer's
   * zone. The calendar still uses the creator zone with the original clock. */
  const {
    displayTimeZone: timeZone,
    calendarTimeZone,
    shiftDisplayToViewer,
    creatorZone,
  } = resolveImplicitCreatorZone(
    pickTimeZoneFields(eventDate.time_zone),
    eventDate.user_time_zone,
  );
  const isAllDay = isTruthyFlag(eventDate.is_all_day);

  /* Newer payloads carry several entries under `dates`; pre-multi-date payloads
   * only have the flat start_/end_ fields, so fall back to a single entry so
   * existing events keep rendering their date card. */
  const rawDateEntries =
    Array.isArray(eventDate.dates) && eventDate.dates.length > 0
      ? eventDate.dates.map((entry) => ({
          startDate: entry?.start_date,
          startTime: entry?.start_time,
          endDate: entry?.end_date,
          endTime: entry?.end_time,
        }))
      : [
          {
            startDate: eventDate.start_date,
            startTime: eventDate.start_time,
            endDate: eventDate.end_date,
            endTime: eventDate.end_time,
          },
        ];
  const displayDateEntries =
    shiftDisplayToViewer && !isAllDay
      ? shiftEntriesToViewerZone(
          rawDateEntries,
          creatorZone,
          resolveViewerTimeZone(viewerTimeZone),
        )
      : rawDateEntries;
  const dates = buildDateGroups(displayDateEntries, locale, {
    hideTime: isAllDay,
  });
  const displayTimeZone = formatGmtOffsetLabel(
    timeZone,
    rawDateEntries[0]?.startDate,
  );

  const calendarName = isFilled(info.title) ? info.title : " ";
  const calendarDateFields = buildCalendarDateFields(rawDateEntries, isAllDay);
  const calendarConfig = {
    name: calendarName,
    description: isFilled(info.summary)
      ? info.summary
      : isFilled(details.description)
        ? details.description
        : " ",
    ...withRecordNames(calendarDateFields, calendarName),
    ...buildCalendarTimeZoneField(calendarTimeZone),
    ...buildCalendarWidgetChrome(calendarDateFields, info.title),
    ...buildCalendarLocationField(address),
  };

  return {
    title: isFilled(info.title) ? info.title : null,
    summary: isFilled(info.summary)
      ? truncate(info.summary, SUMMARY_MAX_LENGTH)
      : null,
    image: toAssetUrl(info.image || data.image),
    links,
    showCalendarButton: hasCalendarDates(calendarConfig),
    moveCalendarToBottom: Boolean(eventDate.move_calendar_to_bottom),
    dates,
    /* Keep the first entry's start/end for backward compatibility with any
     * consumer still reading the single-date fields. */
    start: dates[0]?.start ?? null,
    end: dates[0]?.end ?? null,
    timeZone: displayTimeZone,
    about: isFilled(details.description) ? details.description : null,
    address,
    onShowMap: buildShowMapHandler({
      query: mapQuery,
      latitude: loc.latitude,
      longitude: loc.longitude,
    }),
    contactName: isFilled(contact.contact_name) ? contact.contact_name : null,
    websites,
    phones,
    emails,
    facilities: sortFacilitiesByCanonicalOrder(data.facilities),
    socialBlocks: buildSocialBlocks(
      firstNonEmptyList(data.social_links, data.socialLinks),
    ),
    primaryColor,
    secondaryColor,
    titleFont,
    textFont,
    welcomeImage: toAssetUrl(
      (typeof data.screen === "string" && data.screen) || data.welcome_screen,
    ),
    calendarConfig,
  };
};

/* ------------------------------------------------------------------ flat ---- */

const getFlatDisplay = (data, { viewMoreLabel, locale, viewerTimeZone } = {}) => {
  /*
   * Already-created QR codes may carry the pre-refactor flat shape, where the
   * location lives under `data.location.*` and the schedule under `data.dates.*`
   * (see the removed legacy Event component). `isNestedModel` deliberately does
   * not key off `location`, so those payloads still reach this flat adapter —
   * read the legacy objects as fallbacks so existing events keep rendering
   * their address, date cards, map link and timezone.
   */
  const legacyLoc = data.location || {};
  const legacyDates = data.dates || {};

  /*
   * Multi-link support: newer payloads carry an explicit `links: {label,url}[]`
   * array, which is authoritative — an empty array means "no links", so we do
   * NOT fall back. Older payloads omit the array entirely and only send the
   * first link as `buttonText`/`buttonUrl`; the legacy single-entry fallback
   * kicks in only when no `links` array is present. A missing label defaults to
   * the localized "View More" copy, matching the legacy behaviour.
   */
  const hasLinksArray = Array.isArray(data.links);
  const links = hasLinksArray
    ? data.links
        .map((item) => {
          const url = item?.url;
          const label = isFilled(item?.label)
            ? item.label
            : isFilled(url)
              ? viewMoreLabel || url
              : null;
          if (!isFilled(label) && !isFilled(url)) return null;
          return { label, url: normalizeLinkUrl(url) };
        })
        .filter(Boolean)
    : [];
  if (
    !hasLinksArray &&
    (isFilled(data.buttonText) || isFilled(data.buttonUrl))
  ) {
    links.push({
      label: isFilled(data.buttonText)
        ? data.buttonText
        : viewMoreLabel || data.buttonUrl,
      url: normalizeLinkUrl(data.buttonUrl),
    });
  }

  /* Producers vary: the published payload may send `gmt_time`+`isTimeZone`
   * (display offset or legacy IANA), while the flat event template carries
   * the zone in `event_time_zone` (older flat data used `time_zone`). The
   * on-screen label prefers the first filled value; the calendar widget
   * skips GMT±HH:MM offsets and uses the first IANA id. `user_time_zone` is
   * not a label — it only shifts the clock when no zone was selected. */
  const {
    displayTimeZone: timeZone,
    calendarTimeZone,
    shiftDisplayToViewer,
    creatorZone,
  } = resolveImplicitCreatorZone(
    pickTimeZoneFields(
      data.gmt_time && data.isTimeZone === true ? data.gmt_time : null,
      data.event_time_zone,
      data.time_zone,
      legacyDates.timeZone,
    ),
    data.user_time_zone,
  );

  const websites = firstNonEmptyList(data.contactWebsite, data.websites)
    .map((item) => {
      return buildContactDisplayItem({
        label: item?.title,
        value: item?.website,
        href: normalizeWebsiteHref(item?.website),
      });
    })
    .filter(Boolean);

  const phones = firstNonEmptyList(data.contactMobiles, data.phones)
    .map((item) => {
      return buildContactDisplayItem({
        label: item?.label,
        value: item?.number,
        href: `tel:${item?.number}`,
      });
    })
    .filter(Boolean);

  const emails = firstNonEmptyList(data.contactEmails, data.emails)
    .map((item) => {
      return buildContactDisplayItem({
        label: item?.title,
        value: item?.email,
        href: `mailto:${item?.email}`,
      });
    })
    .filter(Boolean);

  const facilities = sortFacilitiesByCanonicalOrder(
    (data.ficons || [])
      .map((ficon) => FACILITY_KEY_BY_FICON[ficon])
      .filter(Boolean),
  );

  /* The order flag arrives as `street_number` on some producers, as
   * `offer_street_number` on the flat event template, and as
   * `location.street_number` on legacy payloads — honour any of them. The
   * street name/number and remaining parts likewise fall back to the legacy
   * `location.*` fields (number lives under `st_number` there). */
  /* Use nullish coalescing so an explicit `false` order flag is honoured
   * instead of being treated as absent and falling through to another source. */
  const numberFirst =
    data.street_number ??
    data.offer_street_number ??
    legacyLoc.street_number ??
    false;
  const streetName = coalescePart(data.offer_street, legacyLoc.street);
  const streetNo = coalescePart(data.offer_number, legacyLoc.st_number);
  const streetParts = numberFirst
    ? [streetNo, streetName]
    : [streetName, streetNo];
  const address = joinAddress([
    trimmedParts(streetParts).join(" "),
    coalescePart(data.offer_city, legacyLoc.city),
    coalescePart(data.offer_postalcode, legacyLoc.postalcode),
    coalescePart(data.offer_state, legacyLoc.state),
    coalescePart(data.offer_country, legacyLoc.country),
  ]);
  /* ship_address may carry HTML from i18n strings (e.g. "Main St <br>City").
   * Strip tags before using it as a Google Maps query so the markup is not
   * URL-encoded into the lookup; fall back to the structured parts if it
   * sanitizes to empty. */
  const sanitizedShipAddress = isFilled(data.ship_address)
    ? data.ship_address
        .replace(/<br\s*\/?>/gi, " ")
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    : "";
  const mapQuery =
    sanitizedShipAddress ||
    [
      coalescePart(data.offer_street, legacyLoc.street),
      coalescePart(data.offer_number, legacyLoc.st_number),
      coalescePart(data.offer_postalcode, legacyLoc.postalcode),
      coalescePart(data.offer_city, legacyLoc.city),
      coalescePart(data.offer_state, legacyLoc.state),
      coalescePart(data.offer_country, legacyLoc.country),
    ]
      .filter(isFilledPart)
      .join(" ");

  /*
   * Calendar dates must stay ISO. Structured `event_dates` is authoritative
   * when present; otherwise fall back to the flat ISO fields. Do not reuse
   * preformatted `*Text` strings here — those are display-only.
   */
  const structuredCalendarEntries = Array.isArray(data.event_dates)
    ? data.event_dates
    : [];
  const firstStructuredCalendarEntry = structuredCalendarEntries.find(
    (entry) => isFilled(entry?.startDate) || isFilled(entry?.endDate),
  );
  const calendarEntries =
    structuredCalendarEntries.length > 0
      ? structuredCalendarEntries.map((entry) => ({
          startDate: entry?.startDate,
          startTime: entry?.startTime,
          endDate: entry?.endDate,
          endTime: entry?.endTime,
        }))
      : [
          {
            startDate: data.event_startDate || legacyDates.startDate,
            startTime: data.event_startTime || legacyDates.startTime,
            endDate: data.event_endDate || legacyDates.endDate,
            endTime: data.event_endTime || legacyDates.endTime,
          },
        ];

  const isAllDay = isTruthyFlag(
    data.is_all_day ??
      data.isAllDay ??
      data.all_day ??
      firstStructuredCalendarEntry?.isAllDay ??
      firstStructuredCalendarEntry?.is_all_day ??
      legacyDates.isAllDay ??
      legacyDates.is_all_day,
  );

  const calendarName = isFilled(data.title) ? data.title : " ";
  const calendarDateFields = buildCalendarDateFields(calendarEntries, isAllDay);
  const calendarConfig = {
    name: calendarName,
    description: isFilled(data.description) ? data.description : " ",
    ...withRecordNames(calendarDateFields, calendarName),
    /* Calendar needs an IANA id. The on-screen label may be a GMT offset
     * (`gmt_time`); skip those so add-to-calendar-button does not throw.
     * Without an IANA zone the field falls back to the visitor's browser zone
     * (see buildCalendarTimeZoneField). */
    ...buildCalendarTimeZoneField(calendarTimeZone),
    ...buildCalendarWidgetChrome(calendarDateFields, data.title),
    ...buildCalendarLocationField(address),
  };

  /*
   * The preview iframe forwards every date under `event_dates`; older/legacy
   * payloads only carry the first entry via the flat `event_*` / `dates.*`
   * fields, so fall back to a single normalized entry so existing events keep
   * rendering their date card. A producer-supplied preformatted `*Text` string
   * is preferred, then the structured ISO date (localized by formatEventDate).
   */
  const rawDateEntries =
    Array.isArray(data.event_dates) && data.event_dates.length > 0
      ? data.event_dates.map((entry) => ({
          startDate: entry?.startDate,
          startTime: entry?.startTime,
          endDate: entry?.endDate,
          endTime: entry?.endTime,
        }))
      : [
          {
            startDate:
              data.event_startDateText ||
              data.event_startDate ||
              legacyDates.startDateText ||
              legacyDates.startDate,
            startTime: data.event_startTime || legacyDates.startTime,
            endDate:
              data.event_endDateText ||
              data.event_endDate ||
              legacyDates.endDateText ||
              legacyDates.endDate,
            endTime: data.event_endTime || legacyDates.endTime,
          },
        ];
  const displayDateEntries =
    shiftDisplayToViewer && !isAllDay
      ? shiftEntriesToViewerZone(
          rawDateEntries,
          creatorZone,
          resolveViewerTimeZone(viewerTimeZone),
        )
      : rawDateEntries;
  const dates = buildDateGroups(displayDateEntries, locale, {
    hideTime: isAllDay,
  });

  return {
    title: isFilled(data.title) ? data.title : null,
    summary: isFilled(data.description)
      ? truncate(data.description, SUMMARY_MAX_LENGTH)
      : null,
    image: toAssetUrl(data.image),
    links,
    showCalendarButton: hasCalendarDates(calendarConfig),
    moveCalendarToBottom: Boolean(data.move_calendar_to_bottom),
    dates,
    /* Keep the first entry's start/end for backward compatibility with any
     * consumer still reading the single-date fields. */
    start: dates[0]?.start ?? null,
    end: dates[0]?.end ?? null,
    timeZone,
    about: isFilled(data.aboutEvent) ? data.aboutEvent : null,
    address,
    onShowMap: buildShowMapHandler({
      query: mapQuery,
      /* Nullish coalescing so a valid numeric 0 coordinate (equator / prime
       * meridian) isn't discarded in favour of the legacy fallback. */
      latitude: data.latitude ?? legacyLoc.latitude,
      longitude: data.longitude ?? legacyLoc.longitude,
      fallbackUrl: data.offer_url1 || legacyLoc.url,
    }),
    contactName: isFilled(data.contactName) ? data.contactName : null,
    websites,
    phones,
    emails,
    facilities,
    socialBlocks: buildSocialBlocks(data.socialLinks),
    primaryColor: isFilled(data.primaryColor) ? data.primaryColor : null,
    /* The published payload sends the key capitalized (`SecondaryColor`);
     * accept the lowercase spelling defensively too. */
    secondaryColor: isFilled(data.SecondaryColor)
      ? data.SecondaryColor
      : isFilled(data.secondaryColor)
        ? data.secondaryColor
        : null,
    titleFont: quoteFont(data.font_title),
    textFont: quoteFont(data.font_text),
    welcomeImage: toAssetUrl(
      (typeof data.screen === "string" && data.screen) || data.welcome_screen,
    ),
    calendarConfig,
  };
};

export const getEventDisplay = (qrData, options = {}) => {
  const data = qrData || {};
  return isNestedModel(data)
    ? getNestedDisplay(data, options)
    : getFlatDisplay(data, options);
};
